import { isNil } from 'lodash';
import {
  TimeTrackerComponentData,
  TimeTrackerComponentDataValue,
} from '@shared/data/entity/fields/TimeTrackerField';

//#region Public Interface

/**
 * Calculates the total duration in milliseconds between a start date and either an end date or the current time.
 *
 * If the end date is null, the `now` is used instead.
 *
 * @param start The start date.
 * @param end The end date, or null if the range has not terminated.
 * @param now The current time, used to calculate duration if `end` is null.
 * @returns The total duration in milliseconds.
 */
export function calculateDurationFromRange(start: Date, end: Date | null, now: Date): number {
  return Math.max(0, (isNil(end) ? now : end).getTime() - start.getTime());
}

/**
 * Calculates the total duration in milliseconds of a time tracker datum, or the duration from the start to now if
 * the datum is still running.
 *
 * @param datum The time tracker datum.
 * @param now The current time, used to calculate duration if the datum is still running.
 * @returns The total duration in milliseconds.
 */
export function calculateDurationFromTimeTrackerDatum(
  datum: TimeTrackerComponentDataValue,
  now: Date,
): number {
  return calculateDurationFromRange(
    new Date(datum.start.timestamp),
    isNil(datum.end) ? null : new Date(datum.end?.timestamp),
    now,
  );
}

/**
 * Calculates the total duration in milliseconds of a set of time tracker data, including any running data.
 *
 * @param data The time tracker data.
 * @param now The current time, used to calculate duration of any running data.
 * @returns The total duration in milliseconds.
 */
export function calculateDurationFromTimeTrackerData(
  data: TimeTrackerComponentData,
  now: Date,
): number {
  return data.reduce((acc, datum) => acc + calculateDurationFromTimeTrackerDatum(datum, now), 0);
}

/**
 * Formats a duration in milliseconds to a string in the format "d h mm ss", with optional controls for empty units.
 *
 * Examples:
 * - 02m 12s
 * - 1d 2h 00m 04s
 * - 6h 00s
 *
 * @param millis The duration in milliseconds.
 * @param shouldShowEmptyUnits Whether to show units with a value of 0. Seconds are always shown.
 * @returns The formatted duration string.
 **/
export function formatMillisecondsToDurationString(
  millis: number,
  shouldShowEmptyUnits: boolean,
): string {
  const seconds = Math.floor((millis / 1000) % 60);
  const minutes = Math.floor((millis / (1000 * 60)) % 60);
  const hours = Math.floor((millis / (1000 * 60 * 60)) % 24);
  const days = Math.floor(millis / (1000 * 60 * 60 * 24));

  let result = '';

  if (days > 0 || shouldShowEmptyUnits) {
    result += `${days}d `;
  }

  if (days > 0 || hours > 0 || shouldShowEmptyUnits) {
    result += `${hours}h `;
  }

  if (days > 0 || hours > 0 || minutes > 0 || shouldShowEmptyUnits) {
    result += `${pad(minutes)}m `;
  }

  result += `${pad(seconds)}s`;

  return result.trim();
}

/**
 * Determines if a timer is running based on its backing data.
 *
 * @param data The timer data.
 * @returns `true` if the timer is running, `false` otherwise.
 */
export function isTimerRunning(data: { start: any; end?: any }[]): boolean {
  for (const datum of data) {
    if (isNil(datum.end)) {
      return true;
    }
  }

  return false;
}

//#endregion

//#region Private Interface

/**
 * Pads a number with a leading zero if it is less than 10.
 *
 * @param n The number to pad.
 * @returns The padded number as a string.
 */
function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

//#endregion
